// Конвейер вставки целиком: источник (HTML Word/браузера, Markdown, простой текст) -> элементы -> формулы на
// сервере -> структура СТО -> картинки -> проверенные узлы схемы -> ОДНА транзакция редактора (одна отмена).
// Сеанс вставки хранит исходные данные: плашка после вставки может переиграть её в другом режиме
// («только текст», «как в исходнике», ссылки -> источники) без повторной загрузки картинок.
import { isProperWord, properWordsOfTexts } from '../../core/editor/listCase';
import type { JSONContent as J } from '@tiptap/core';
import { Fragment, Slice, type Node as PMNode } from '@tiptap/pm/model';
import { NodeSelection, Selection, TextSelection } from '@tiptap/pm/state';
import type { Ctx } from '../../core/registry';
import type { Meta, Source } from '../../core/types';
import { refreshNumbering } from '../../core/editor/numberingPlugin';
import { newId } from '../../core/schema/util';
import { plainSpaces, plainSpacesDeep } from '../../core/schema/constants';
import { unboldAll, unboldBody } from '../../core/schema/clean';
import { prepHtml, APP_LABEL, type Prepared, type SourceApp } from './prep';
import { DomReader, inlineText, textNode } from './dom';
import { StructBuilder, bestForms, collectForms, type LinkMode, type NewSource, type PasteStats } from './structure';
import { resolveImages, resolveMaths, type ImageReport } from './resolve';
import { dollarDelims, looksMarkdown, plainTextToHtml, textToParagraphs } from './text';
import { isCalcLine } from '../math/latex';

export type Mode = 'structure' | 'basic' | 'text';

export interface PasteInput {
  kind: 'paste' | 'drop' | 'command';
  html: string;
  text: string;
  files: File[];
  pos: number | null;
  /** язык из метаданных редактора кода (VS Code: vscode-editor-data.mode) */
  codeLang?: string;
  /** принудительно: Markdown */
  markdown?: boolean;
}
export interface PasteOpts { mode: Mode; links: LinkMode }

export interface Session {
  input: PasteInput;
  opts: PasteOpts;
  app: SourceApp;
  stats: PasteStats | null;
  images: ImageReport | null;
  mathErrors: number;
  cache: Map<string, string>;
  docAfter: PMNode | null;
  addedSources: string[];
  metaBefore: Partial<Meta> | null;
  hasLinks: boolean;
  blocksCount: number;
  /** вставка готового фрагмента (импорт .docx): переиграть в другом режиме нельзя, только отменить */
  fixed?: boolean;
}

/** Ход длинной вставки (плашка «Вставка…»): формулы, картинки. */
export const progress = { set: (_t: string) => { /* задаёт плашка */ } };

const today = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
};

// ------------------------------------------------------------------------------------------------ разбор
interface Parsed {
  app: SourceApp;
  blocks: J[];
  appendices: J[];
  sources: NewSource[];
  meta: Record<string, any>;
  stats: PasteStats | null;
  images: ImageReport | null;
  mathErrors: number;
  hasLinks: boolean;
}

/** Разобрать вход в узлы (без вставки). Экспортируется для автотестов (window.stoPaste.parse). */
export async function parseInput(ctx: Ctx, input: PasteInput, opts: PasteOpts, cache = new Map<string, string>(),
  where?: { pos: number }): Promise<Parsed> {
  const pid = ctx.store.getState().projectId!;
  if (opts.mode === 'text') {
    const text = input.text || htmlText(input.html);
    const blocks = textToParagraphs(text).map((t) => ({ type: 'paragraph', content: [{ type: 'text', text: t }] }));
    return { app: 'text', blocks, appendices: [], sources: [], meta: {}, stats: null, images: null, mathErrors: 0, hasLinks: false };
  }
  let prep: Prepared;
  let app: SourceApp;
  const html = input.html && !/^\s*<meta[^>]*>\s*$/i.test(input.html) ? input.html : '';
  if (html && !(input.markdown && input.text)) {
    prep = prepHtml(html);
    app = prep.app;
    // VS Code и др.: HTML - это подсвеченный код; если это Markdown-файл - разбираем текст как Markdown
    if (input.codeLang === 'markdown' && input.text) return parseInput(ctx, { ...input, html: '', markdown: true }, opts, cache, where);
  } else {
    const text = input.text || '';
    if (input.markdown || looksMarkdown(text)) {
      const r = await ctx.api.post<any>(`/api/projects/${encodeURIComponent(pid)}/paste/markdown`, { text: dollarDelims(text) });
      if (r.kind === 'writer') {
        const stats = emptyStats();
        for (const b of r.blocks) countBlock(b, stats);
        return { app: 'markdown', blocks: r.blocks, appendices: r.blocks.filter((b: J) => b.type === 'appendix'),
          sources: r.sources || [], meta: {}, stats, images: null, mathErrors: 0, hasLinks: false };
      }
      prep = prepHtml(r.html);
      app = 'markdown';
    } else {
      prep = prepHtml(plainTextToHtml(text));
      app = 'text';
    }
    prep.app = app;
  }
  const dom = new DomReader(prep).read();
  if (dom.maths.length > 5) progress.set(`преобразование формул (${dom.maths.length})…`);
  const mathErrors = await resolveMaths(ctx, pid, prep.omml, dom.maths.filter((m) => m.error !== 'ole'));
  const ed = ctx.editor;
  const pos = where?.pos ?? input.pos ?? ed?.state.selection.from ?? 0;
  const forms = bestForms(collectForms([
    ...dom.items.filter((x) => x.k === 'p' && !x.hLevel).map((x: any) => x.text as string),
    ...docTexts(ed?.state.doc, 1500),
  ]));
  const builder = new StructBuilder(dom, {
    mode: opts.mode === 'basic' ? 'basic' : 'structure', links: opts.links, app,
    ctxLevel: ed ? headingLevelAt(ed.state.doc, pos) : 0, forms, today: today(),
  });
  const res = builder.build();
  const images = await resolveImages(ctx, pid, res.figures, input.files, cache, (d, t) => {
    if (t >= 3) progress.set(`картинки: ${d} из ${t}…`);
  });
  res.stats.missingImages += images.failed;
  if (prep.commentsRemoved) res.stats.commentsRemoved = prep.commentsRemoved;
  return { app, blocks: res.blocks, appendices: res.appendices, sources: res.sources, meta: res.meta, stats: res.stats,
    images, mathErrors, hasLinks: dom.links.length > 0 };
}

function htmlText(html: string): string {
  const doc = new DOMParser().parseFromString(prepHtml(html).html, 'text/html');
  doc.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6, tr, br').forEach((el) => el.append('\n'));
  return doc.body.textContent || '';
}

function docTexts(doc: PMNode | undefined, limit: number): string[] {
  const out: string[] = [];
  doc?.descendants((n) => {
    if (out.length >= limit) return false;
    if (n.type.name === 'paragraph') { out.push(n.textContent); return false; }
    return true;
  });
  return out;
}

function headingLevelAt(doc: PMNode, pos: number): number {
  let level = 0;
  doc.forEach((n, off) => {
    if (off >= pos) return;
    if (n.type.name === 'heading') level = n.attrs.level;
    else if (n.type.name === 'structHeading') level = 1;
  });
  return level;
}

function countObjects(doc: PMNode): { figure: number; stoTable: number; formula: number } {
  const c = { figure: 0, stoTable: 0, formula: 0 };
  doc.descendants((n) => {
    if (n.type.name in c) (c as any)[n.type.name]++;
    return !['figure', 'formula', 'listing'].includes(n.type.name);
  });
  return c;
}

function emptyStats(): PasteStats {
  return { headings: 0, struct: 0, paragraphs: 0, lists: 0, figures: 0, tables: 0, formulas: 0, listings: 0, footnotes: 0,
    notes: 0, xrefs: 0, cites: 0, sources: 0, appendices: 0, missingImages: 0, emptyRemoved: 0, numbersRemoved: 0,
    recased: 0, links: 0, lostInTables: 0, titleSkipped: false, tocSkipped: false, listsFixed: 0 };
}
function countBlock(b: J, s: PasteStats) {
  const t = b.type;
  if (t === 'heading') s.headings++; else if (t === 'structHeading') s.struct++; else if (t === 'figure') s.figures++;
  else if (t === 'stoTable') s.tables++; else if (t === 'formula') s.formulas++; else if (t === 'listing') s.listings++;
  else if (t === 'stoList') s.lists++; else if (t === 'paragraph') s.paragraphs++; else if (t === 'appendix') s.appendices++;
  if (t === 'appendix') (b.content || []).forEach((c) => countBlock(c, s));
}

// ------------------------------------------------------------------------------------------------ чистка JSON
/** Перенести результаты формул/картинок в attrs, убрать служебные поля и пустое, гарантировать схему.
 *  Неразрывные пробелы и дефисы (Word ставит их сам, «&nbsp;» веб-страниц) - обычными: без USE_NBSP их нет в DOCX
 *  (core/schema/constants, как lib/sto_lib.USE_NBSP). */
export function finalize(nodes: J[]): J[] {
  const fix = (n: J): J | null => {
    const a: any = n;
    if (n.type === 'text') {
      if (!n.text) return null;
      const t: J = { type: 'text', text: plainSpaces(n.text) };
      if (n.marks?.length) t.marks = n.marks;
      return t;
    }
    if (a.__wikiRef !== undefined) return null;
    if (n.type === 'mathInline') {
      const m = a.__math;
      return { type: 'mathInline', attrs: { latex: plainSpaces(String(m?.latex ?? n.attrs?.latex ?? '')), mathml: m?.mathml || null } };
    }
    if (n.type === 'formula') {
      const parts: J[] | undefined = a.__parts;
      let latex = parts ? parts.map((p: any) => String(p.__math?.latex ?? p.attrs?.latex ?? '')).filter(Boolean).join(' \\quad ') :
        String(a.__math?.latex ?? n.attrs?.latex ?? '');
      latex = latex.trim().replace(/[,;]\s*$/, '');
      const mm = parts && parts.length === 1 ? (parts[0] as any).__math?.mathml : a.__math?.mathml;
      // 4.6.1.6: выключную формулу нумеруют; без номера - только строка расчёта с подставленными значениями (4.6.1.8)
      const numbered = !!n.attrs?.numbered || (!!latex && !isCalcLine(latex));
      return { type: 'formula', attrs: { ...clean(n.attrs), latex: plainSpaces(latex), numbered, mathml: mm || null, where: plainSpacesDeep(n.attrs?.where || []) } };
    }
    if (n.type === 'footnote') {
      // 4.10.11: в конце текста сноски точка не ставится (Word, Google, LibreOffice её ставят)
      const content = (n.attrs?.content || []).map(fix).filter(Boolean) as J[];
      const last = content[content.length - 1];
      if (last?.type === 'text' && /[^.]\.\s*$/.test(last.text || '') && !/(?:^|[\s ])(?:\S|др|т\.[\s ]?[дпе])\.\s*$/.test(last.text || '')) {
        last.text = (last.text || '').replace(/\.\s*$/, '');
      }
      return { type: 'footnote', attrs: { ...clean(n.attrs), content } };
    }
    const out: J = { type: n.type };
    if (n.attrs) out.attrs = clean(n.attrs);
    if (n.marks) out.marks = n.marks;
    if (n.content) {
      const c = n.content.map(fix).filter(Boolean) as J[];
      if (c.length) out.content = c;
    }
    // обязательное содержимое
    if (n.type === 'stoListItem' && out.content?.[0]?.type !== 'paragraph') out.content = [{ type: 'paragraph' }, ...(out.content || [])];
    if ((n.type === 'tableCell' || n.type === 'tableHeader') && !out.content) out.content = [{ type: 'paragraph' }];
    if (n.type === 'figure' && !out.content) out.content = [{ type: 'caption' }];
    if (n.type === 'listing' && !out.content) out.content = [{ type: 'caption' }];
    if ((n.type === 'note' || n.type === 'example' || n.type === 'tableNote') && !out.content) out.content = [{ type: 'paragraph' }];
    if (n.type === 'appendix' && out.content?.[0]?.type !== 'appendixTitle') out.content = [{ type: 'appendixTitle' }, ...(out.content || [])];
    if (n.type === 'stoList' && !out.content) return null;
    if (n.type === 'tableRow' && !out.content) return null;
    if (n.type === 'table' && !out.content) return null;
    if (n.type === 'stoTable' && !out.content?.some((c) => c.type === 'table')) return null;
    return out;
  };
  return lowerListItems(capListDepth(nodes.map(fix).filter(Boolean) as J[]));
}

/** 4.4.3, 4.4.4: текст позиции перечисления - со строчной буквы («Включить стенд» -> «включить стенд»), кроме имён
 *  собственных (по написанию во вставленном тексте и словарю), аббревиатур и латиницы - как у sto fix. */
function lowerListItems(nodes: J[]): J[] {
  const paras: string[] = [];
  const textOf = (n: J): string => (n.type === 'text' ? n.text || '' : (n.content || []).map(textOf).join(''));
  const collect = (n: J) => {
    if (n.type === 'paragraph' || n.type === 'heading') { paras.push(textOf(n)); return; }
    (n.content || []).forEach(collect);
  };
  nodes.forEach(collect);
  const proper = properWordsOfTexts(paras);
  const walk = (n: J) => {
    if (n.type === 'stoListItem') {
      const p = n.content?.[0];
      const first = p?.type === 'paragraph' ? p.content?.[0] : undefined;
      if (first && first.type === 'text' && !(first.marks || []).some((m: any) => m.type === 'code')) {
        const t = first.text || '';
        const m = t.match(/^([\s\u00a0«"(]*)([А-ЯЁ][А-Яа-яЁё-]*)/);
        if (m && !isProperWord(m[2], textOf(p!).slice(m[0].length), proper)) {
          const at = m[1].length;
          first.text = t.slice(0, at) + t[at].toLowerCase() + t.slice(at + 1);
        }
      }
    }
    (n.content || []).forEach(walk);
  };
  nodes.forEach(walk);
  return nodes;
}

/** 4.4.4: перечисление и его детализация - не больше двух уровней; третий и глубже поднимаются на второй. */
function capListDepth(nodes: J[]): J[] {
  const cap = (n: J, depth: number): J => {
    const items: J[] = [];
    for (const item of n.content || []) {
      const sub = (item.content || []).find((c) => c.type === 'stoList');
      if (sub && depth >= 1) {
        items.push({ ...item, content: (item.content || []).filter((c) => c !== sub) });
        items.push(...(cap(sub, depth).content || []));
      } else {
        items.push({ ...item, content: (item.content || []).map((c) => (c.type === 'stoList' ? cap(c, depth + 1) : c)) });
      }
    }
    return { ...n, content: items };
  };
  const walk = (n: J): J => (n.type === 'stoList' ? cap(n, 0) : n.content ? { ...n, content: n.content.map(walk) } : n);
  return nodes.map(walk);
}
function clean(attrs: Record<string, any> | undefined): Record<string, any> {
  const o: Record<string, any> = {};
  for (const [k, v] of Object.entries(attrs || {})) if (!k.startsWith('__') && v !== undefined) o[k] = plainSpacesDeep(v);
  return o;
}

/** JSON -> узел ProseMirror; при ошибке схемы - абзацы с текстом (чтобы ничего не потерять). */
function toNodes(schema: any, blocks: J[]): PMNode[] {
  const out: PMNode[] = [];
  for (const b of blocks) {
    try {
      const n = schema.nodeFromJSON(b);
      n.check();
      out.push(n);
    } catch (e) {
      console.warn('вставка: узел не по схеме, вставлен текстом', b, e);
      const text = plainOf(b);
      if (text) out.push(schema.nodeFromJSON({ type: 'paragraph', content: [{ type: 'text', text }] }));
    }
  }
  return out;
}
function plainOf(n: J): string {
  if (n.type === 'text') return n.text || '';
  if (n.type === 'mathInline' || n.type === 'formula') return `$${n.attrs?.latex || ''}$`;
  return (n.content || []).map(plainOf).join(n.type === 'tableRow' ? ' | ' : ' ').replace(/\s+/g, ' ').trim();
}

/** Блоки -> только абзацы (для вставки в ячейку таблицы). */
function asParagraphs(blocks: J[]): J[] {
  const out: J[] = [];
  const walk = (b: J) => {
    if (b.type === 'paragraph') { out.push(b); return; }
    if (b.type === 'formula') { out.push({ type: 'paragraph', content: [{ type: 'mathInline', attrs: { latex: b.attrs?.latex || '' } }] }); return; }
    if (b.type === 'listing') { for (const l of String(b.attrs?.code || '').split('\n')) out.push({ type: 'paragraph', content: l ? [textNode(l, { code: true })] : undefined }); return; }
    if (b.type === 'figure' || b.type === 'pageBreak') return;
    if (b.type === 'heading' || b.type === 'caption' || b.type === 'appendixTitle') { if (b.content) out.push({ type: 'paragraph', content: b.content }); return; }
    if (b.type === 'stoListItem') {
      const p = b.content?.[0];
      out.push({ type: 'paragraph', content: [textNode('– ', {}), ...(p?.content || [])] });
      (b.content || []).slice(1).forEach(walk);
      return;
    }
    if (b.type === 'tableRow') { const t = plainOf(b); if (t) out.push({ type: 'paragraph', content: [{ type: 'text', text: t }] }); return; }
    (b.content || []).forEach(walk);
  };
  blocks.forEach(walk);
  return out;
}

// ------------------------------------------------------------------------------------------------ вставка
type Where = 'block' | 'cell' | 'inline';

function whereAt(sel: Selection): Where {
  const $f = sel.$from;
  for (let d = $f.depth; d > 0; d--) {
    const t = $f.node(d).type.name;
    if (t === 'tableCell' || t === 'tableHeader') return 'cell';
    if (t === 'caption' || t === 'heading' || t === 'appendixTitle') return 'inline';
  }
  return 'block';
}

const RICH_TYPES = new Set(['heading', 'structHeading', 'figure', 'stoTable', 'listing', 'formula', 'note', 'example', 'pageBreak']);

/** Вставка, которую нельзя без потерь уложить в строку заголовка/подписи или в ячейку таблицы. */
function isRich(main: J[], where: Where): boolean {
  if (!main.length) return false;
  if (where === 'cell') {
    // формула или перечисление в ячейке - нормально; заголовки, рисунки, таблицы, листинги - нет
    return main.length > 30 || main.some((b) => ['heading', 'structHeading', 'figure', 'stoTable', 'listing'].includes(b.type || ''));
  }
  if (main.some((b) => b.type && RICH_TYPES.has(b.type))) {
    // один заголовок в заголовок/подпись - это просто его текст
    return !(where === 'inline' && main.length === 1 && main[0].type === 'heading');
  }
  // в заголовок/подпись - только одна строка; в ячейку - абзацы и перечисления (как в Word)
  return where === 'inline' ? main.length > 1 || main[0].type !== 'paragraph' : main.length > 30;
}

/** Вставить блоки одной транзакцией. Приложения - в конец документа. Возвращает число вставленных узлов. */
export function insertParsed(ctx: Ctx, blocks: J[], appendices: J[], pos: number | null): number {
  const ed = ctx.editor;
  if (!ed) return 0;
  const { state, view } = ed;
  const schema = state.schema;
  const tr = state.tr;
  if (pos !== null && pos !== undefined) {
    const p = Math.max(0, Math.min(pos, tr.doc.content.size));
    tr.setSelection(Selection.near(tr.doc.resolve(p)));
  } else if (tr.selection instanceof NodeSelection) {
    tr.setSelection(Selection.near(tr.doc.resolve(tr.selection.to)));
  }
  let where: Where = whereAt(tr.selection);
  let main = blocks.filter((b) => b.type !== 'appendix');
  // «богатая» вставка (несколько блоков, рисунки, таблицы, формулы, заголовки) в заголовок, подпись или ячейку не
  // склеивается в одну строку: блоки встают ПОСЛЕ текущего объекта (пустой заголовок заменяется вставкой целиком)
  let blockAt: { from: number; to: number } | null = null;
  if (where !== 'block' && isRich(main, where)) {
    const $f = tr.selection.$from;
    let d = $f.depth;
    while (d > 1 && !['doc', 'appendix'].includes($f.node(d - 1).type.name)) d--;
    const top = $f.node(d);
    const emptyHead = top.type.name === 'heading' && !top.textContent.trim() && !top.childCount;
    blockAt = emptyHead ? { from: $f.before(d), to: $f.after(d) } : { from: $f.after(d), to: $f.after(d) };
    where = 'block';
  }
  if (where === 'cell') main = asParagraphs(main);
  if (where === 'inline') {
    const inl: J[] = [];
    for (const p of asParagraphs(main)) { if (inl.length) inl.push({ type: 'text', text: ' ' }); inl.push(...(p.content || [])); }
    main = inl.length ? [{ type: 'paragraph', content: inl }] : [];
  }
  // полужирный вне заголовков: метки «Цель работы:» и абзацы, целиком полужирные, - обычным (core/schema/clean.ts)
  const fin = finalize(main);
  const nodes = toNodes(schema, where === 'inline' ? fin : unboldBody(fin, true));
  let count = 0;
  if (nodes.length) {
    if (blockAt) {
      tr.replaceWith(blockAt.from, blockAt.to, Fragment.fromArray(nodes));
      const end = blockAt.from + Fragment.fromArray(nodes).size;
      tr.setSelection(Selection.near(tr.doc.resolve(Math.min(end, tr.doc.content.size)), -1));
    } else {
      const openStart = nodes[0].type.name === 'paragraph' ? 1 : 0;
      const openEnd = nodes[nodes.length - 1].type.name === 'paragraph' ? 1 : 0;
      const nSteps = tr.steps.length;
      tr.replaceSelection(new Slice(Fragment.fromArray(nodes), openStart, openEnd));
      // конец вставленного: ProseMirror после блока (перечисления, таблицы) ищет выделение ВПЕРЁД и выделял
      // следующий узел - например, заголовок «Список использованных источников»; дальнейший набор уходил туда
      // (или заменял его). Берём позицию сразу за вставкой и ищем текст НАЗАД - внутри вставленного.
      if (tr.steps.length > nSteps) {
        let end = -1;
        tr.mapping.maps[tr.steps.length - 1].forEach((_a, _b, _s, e) => { if (end < 0) end = e; });
        if (end >= 0) tr.setSelection(Selection.near(tr.doc.resolve(Math.min(end, tr.doc.content.size)), -1));
      }
    }
    count += nodes.length;
    // курсор - после вставленного, в абзаце (как в Word): если вставка кончилась таблицей/рисунком - новый абзац
    const $e = tr.selection.$to;
    let d = $e.depth;
    while (d > 0 && !['doc', 'appendix'].includes($e.node(d - 1).type.name)) d--;
    if (where === 'block' && d > 0 && $e.node(d).type.name !== 'paragraph') {
      const after = $e.after(d);
      const next = tr.doc.resolve(after).nodeAfter;
      if (!next || next.type.name !== 'paragraph') tr.insert(after, schema.nodes.paragraph.create());
      tr.setSelection(TextSelection.create(tr.doc, after + 1));
    }
  }
  const apps = where === 'block' ? toNodes(schema, unboldBody(finalize(appendices), true)) : [];
  if (apps.length) { tr.insert(tr.doc.content.size, Fragment.fromArray(apps)); count += apps.length; }
  if (!count) return 0;
  tr.setMeta('paste', true).setMeta('uiEvent', 'paste').scrollIntoView();
  view.dispatch(tr);
  view.focus();
  return count;
}

// ------------------------------------------------------------------------------------------------ сеанс
const norm = (s = '') => s.toLowerCase().replace(/ё/g, 'е').replace(/[^a-zа-я0-9]+/g, '');

/** Добавить источники в библиотеку (без дублей: тот же текст или адрес) и заменить ссылки cite на них. */
function mergeSources(ctx: Ctx, found: NewSource[], blocks: J[]): string[] {
  if (!found.length) return [];
  const lib = ctx.store.getState().sources;
  const remap = new Map<string, string>();
  const added: Source[] = [];
  for (const s of found) {
    const same = lib.find((x) => (s.url && x.url === s.url) || (s.text && norm(x.text || x.formatted) === norm(s.text)) ||
      (!s.text && !s.url && x.id === s.id)) || (lib.find((x) => x.id === s.id) && !s.text && !s.url ? lib.find((x) => x.id === s.id) : undefined);
    if (same) { remap.set(s.id, same.id); continue; }
    let id = s.id;
    if (lib.some((x) => x.id === id) || added.some((x) => x.id === id)) { id = 's' + newId().slice(1); remap.set(s.id, id); }
    added.push({ ...(plainSpacesDeep(s) as any), id, kind: (s.kind || 'other') as Source['kind'] });
  }
  if (remap.size) {
    const walk = (n: J) => {
      if (n.type === 'cite') n.attrs = { ...n.attrs, items: (n.attrs?.items || []).map((it: any) => ({ ...it, source: remap.get(it.source) || it.source })) };
      if (n.type === 'footnote') (n.attrs?.content || []).forEach(walk);
      (n.content || []).forEach(walk);
    };
    blocks.forEach(walk);
  }
  if (added.length) {
    ctx.store.getState().setSources((l) => [...l, ...added]);
    // строки по ГОСТ для веб-источников - на сервере
    const meta = ctx.store.getState().meta;
    ctx.api.formatSources(ctx.store.getState().sources, meta?.options.sources_order || 'alpha').then((r) => {
      const by = new Map(r.items.map((x) => [x.id, x.text]));
      ctx.store.getState().setSources((l) => l.map((x) => (added.some((a) => a.id === x.id) && by.get(x.id) ? { ...x, formatted: by.get(x.id) } : x)));
      refreshNumbering();
    }).catch(() => { /* без сети - строка появится при следующем форматировании */ });
  }
  return added.map((s) => s.id);
}

function applyMeta(ctx: Ctx, patch: Record<string, any>): Partial<Meta> | null {
  const meta = ctx.store.getState().meta;
  if (!meta || !Object.keys(patch).length) return null;
  const before: Partial<Meta> = {};
  const next: Meta = { ...meta };
  for (const k of ['title', 'discipline', 'number', 'variant'] as const) {
    if (patch[k] && !String(meta[k] || '').trim()) { (before as any)[k] = meta[k]; (next as any)[k] = plainSpacesDeep(patch[k]); }
  }
  // листы из вставленного .docx (import_docx_fragment): «Задание», «Реферат», «Аннотация», «Сведения о
  // самостоятельности…» (indep - может не быть в проектах, созданных до этого поля)
  for (const k of ['zadanie', 'referat', 'annotation', 'indep'] as const) {
    const cur: any = meta[k] || { enabled: false, doc: { type: 'doc', content: [] } };
    const empty = !cur?.enabled || !(cur.doc?.content || []).some((c: J) => c.content?.length);
    if (patch[k] && empty) {
      (before as any)[k] = cur;
      (next as any)[k] = { ...cur, enabled: true, doc: unboldAll(plainSpacesDeep(patch[k])), ...(k === 'zadanie' && patch.zadanie_title ? { title: plainSpaces(patch.zadanie_title) } : {}) };
    }
  }
  if (!Object.keys(before).length) return null;
  ctx.store.getState().setMeta(() => next);
  return before;
}

export async function runPaste(ctx: Ctx, input: PasteInput, opts: PasteOpts, cache = new Map<string, string>()): Promise<Session | null> {
  const ed = ctx.editor;
  if (!ed || !ctx.store.getState().projectId) return null;
  const pos = input.pos;
  const parsed = await parseInput(ctx, input, opts, cache, pos !== null ? { pos } : undefined);
  const blocks = parsed.blocks.filter((b) => b.type !== 'appendix');
  const apps = parsed.app === 'markdown' ? parsed.appendices : parsed.appendices;
  const addedSources = mergeSources(ctx, parsed.sources, [...blocks, ...apps]);
  const before = countObjects(ed.state.doc);
  const n = insertParsed(ctx, blocks, apps, pos);
  // честная сводка: сколько объектов реально попало в документ по сравнению с распознанным
  if (n && parsed.stats) {
    const after = countObjects(ctx.editor!.state.doc);
    const want = { figure: 0, stoTable: 0, formula: 0 };
    const walk = (b: J) => { if (b.type && b.type in want) (want as any)[b.type]++; (b.content || []).forEach(walk); };
    [...blocks, ...apps].forEach(walk);
    const lost: string[] = [];
    const chk = (k: keyof typeof after, name: string) => {
      const got = after[k] - before[k];
      if (want[k] > got) lost.push(`${name}: ${want[k] - got}`);
    };
    chk('figure', 'рисунков');
    chk('stoTable', 'таблиц');
    chk('formula', 'выключных формул');
    if (lost.length) ctx.toast(`Вставлено не всё распознанное (${lost.join(', ')}): объекты превращены в текст - вставьте фрагмент в пустой абзац`, 'warn', 12000);
  }
  const metaBefore = applyMeta(ctx, parsed.meta);
  if (addedSources.length || metaBefore) refreshNumbering();
  if (parsed.images?.failed) {
    ctx.toast(`Не загружено картинок: ${parsed.images.failed} - на их месте рамки «вставьте рисунок». ${parsed.images.reasons[0] || ''}`, 'warn', 9000);
  }
  if (parsed.mathErrors) ctx.toast(`Формул не распознано: ${parsed.mathErrors} - они вставлены текстом, проверьте их`, 'warn', 8000);
  if (parsed.stats?.oleEquations) {
    ctx.toast(`Формулы Equation 3.0 / MathType (${parsed.stats.oleEquations}) в буфере есть только картинкой - на их месте пустые формулы: наберите их в редакторе формул (двойной щелчок)`, 'warn', 12000);
  }
  return {
    input, opts, app: parsed.app, stats: parsed.stats, images: parsed.images, mathErrors: parsed.mathErrors, cache,
    docAfter: n ? ctx.editor!.state.doc : null, addedSources, metaBefore, hasLinks: parsed.hasLinks, blocksCount: n,
  };
}

/** Отменить вставку сеанса (если после неё документ не менялся): отмена в редакторе + источники и данные работы. */
export function undoSession(ctx: Ctx, s: Session): boolean {
  const ed = ctx.editor;
  if (!ed || !s.docAfter || !ed.state.doc.eq(s.docAfter)) return false;
  ed.commands.undo();
  if (s.addedSources.length) ctx.store.getState().setSources((l) => l.filter((x) => !s.addedSources.includes(x.id)));
  if (s.metaBefore) ctx.store.getState().setMeta((m) => ({ ...m, ...s.metaBefore }));
  refreshNumbering();
  return true;
}

// ------------------------------------------------------------------------------------------------ сводка
function plural(n: number, one: string, few: string, many: string) {
  const a = n % 10, b = n % 100;
  const w = a === 1 && b !== 11 ? one : a >= 2 && a <= 4 && (b < 12 || b > 14) ? few : many;
  return `${n} ${w}`;
}

export function summary(s: Session): { title: string; parts: string[]; cleaned: string[] } {
  const title = s.opts.mode === 'text' ? 'Вставлено как простой текст' : `Вставлено ${APP_LABEL[s.app] || ''}`.trim();
  const st = s.stats;
  const parts: string[] = [];
  const cleaned: string[] = [];
  if (!st) return { title, parts, cleaned };
  const add = (n: number, one: string, few: string, many: string) => { if (n) parts.push(plural(n, one, few, many)); };
  add(st.headings + st.struct, 'заголовок', 'заголовка', 'заголовков');
  add(st.paragraphs, 'абзац', 'абзаца', 'абзацев');
  add(st.lists, 'перечисление', 'перечисления', 'перечислений');
  add(st.figures, 'рисунок', 'рисунка', 'рисунков');
  add(st.tables, 'таблица', 'таблицы', 'таблиц');
  add(st.formulas, 'формула', 'формулы', 'формул');
  add(st.listings, 'листинг', 'листинга', 'листингов');
  add(st.footnotes, 'сноска', 'сноски', 'сносок');
  add(st.notes, 'примечание', 'примечания', 'примечаний');
  add(st.appendices, 'приложение', 'приложения', 'приложений');
  add(st.sources, 'источник', 'источника', 'источников');
  if (st.xrefs) parts.push(`ссылок на объекты: ${st.xrefs}`);
  if (st.cites) parts.push(`ссылок на источники: ${st.cites}`);
  if (s.app === 'word' || s.app === 'libre' || s.app === 'gdocs' || s.app === 'web' || s.app === 'excel') cleaned.push('шрифты, цвета и отступы');
  if (st.numbersRemoved) cleaned.push(`ручные номера (${st.numbersRemoved}) - теперь нумерация живая`);
  if (st.emptyRemoved) cleaned.push(`пустые абзацы (${st.emptyRemoved})`);
  if (st.recased) cleaned.push(`капс и сбитый регистр в заголовках (${st.recased})`);
  if (st.listsFixed) cleaned.push(`нумерация «1)» на первом уровне перечислений - теперь «а)» (4.4)`);
  if (st.titleSkipped) cleaned.push('титульный лист (строится сам по данным работы)');
  if (st.titleMoved) cleaned.push('название документа - в тему работы (титул)');
  if (st.tocSkipped) cleaned.push('оглавление (строится само)');
  if (st.missingImages) parts.push(`мест под рисунки: ${st.missingImages}`);
  // графика .docx (B3): диаграммы перерисованы по данным, формулы Equation 3.0 переведены, фигуры Word
  const sx = st as any;
  if (sx.chartsRendered) parts.push(`диаграмм перерисовано по данным: ${sx.chartsRendered}`);
  if (sx.oleConverted) parts.push(`формул Equation 3.0/MathType переведено: ${sx.oleConverted}`);
  if (sx.shapesRendered) parts.push(`рисунков из фигур Word отрисовано: ${sx.shapesRendered}`);
  if (sx.figuresGrouped) parts.push(`рисунков из нескольких частей: ${sx.figuresGrouped}`);
  if (st.noCaption) parts.push(`введите названия: ${st.noCaption} (рисунки и таблицы без подписи)`);
  // дописанное сайтом при загрузке .docx (ссылки, абзацы-ссылки, названия - stoweb/autorefs.py): выделено пунктиром
  if (sx.autoAdded) parts.push(`добавлено автоматически (выделено - проверьте): ${[sx.refsAdded ? `ссылок ${sx.refsAdded}` : '', sx.namesAdded ? `названий ${sx.namesAdded}` : ''].filter(Boolean).join(', ')}`);
  if (st.oleEquations) parts.push(`формул-картинок набрать заново: ${st.oleEquations}`);
  if (st.lostInTables) parts.push(`картинок в ячейках пропущено: ${st.lostInTables}`);
  if (st.imagesFromTables) parts.push(`картинки из ячеек таблиц - рисунками после таблицы: ${st.imagesFromTables}`);
  if (st.nestedTables) parts.push(`вложенные таблицы - текстом в ячейке: ${st.nestedTables}`);
  if (st.commentsRemoved) cleaned.push(`примечания рецензента (${st.commentsRemoved})`);
  return { title, parts, cleaned };
}

export { inlineText };
